import React, { FormEventHandler, KeyboardEventHandler, RefObject } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Form,
  FormText,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import MessageBubble from "./MessageBubble";
import {
  ActivityItem,
  CannedResponse,
  MessageGroup,
  TicketAttachment,
  TicketMessage,
  TicketTab,
} from "./types";
import { formatDateTime, formatFileSize, getUserInitials } from "./utils";

type MessageComposerProps = {
  canViewInternalNotes: boolean;
  isInternalNote: boolean;
  messageBody: string;
  messageFileInputKey: number;
  messageFiles: File[];
  sendingMessage: boolean;
  messageComposerDisabled: boolean;
  sendMessageShortcutLabel: string;
  onMessageBodyChange: (value: string) => void;
  onMessageInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onMessageFilesChange: (files: File[]) => void;
  onRemoveMessageFile: (fileIndex: number) => void;
  onToggleInternalNote: () => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const MessageComposer = ({
  canViewInternalNotes,
  isInternalNote,
  messageBody,
  messageFileInputKey,
  messageFiles,
  sendingMessage,
  messageComposerDisabled,
  sendMessageShortcutLabel,
  onMessageBodyChange,
  onMessageInputKeyDown,
  onMessageFilesChange,
  onRemoveMessageFile,
  onToggleInternalNote,
  onSubmit,
}: MessageComposerProps) => (
  <Card className="shadow-none border mt-3 mb-0">
    <CardBody>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="card-title mb-1">Reply</h5>
          <small className="text-muted">
            Press {sendMessageShortcutLabel} to send. Enter creates a new line.
          </small>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {isInternalNote && (
            <Badge color="warning">
              Internal note mode
            </Badge>
          )}
        </div>
      </div>

      <Form onSubmit={onSubmit}>
        <Input
          type="textarea"
          rows={5}
          value={messageBody}
          onChange={(event) => onMessageBodyChange(event.target.value)}
          onKeyDown={onMessageInputKeyDown}
          placeholder="Write a clear update, ask a targeted question, or send the resolution details..."
          className="mb-3"
        />

        <Row className="g-3">
          <Col lg={8}>
            <Label className="form-label">Attachments</Label>
            <Input
              key={messageFileInputKey}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip,.rar,.7z,image/*,application/pdf"
              onChange={(event) => {
                onMessageFilesChange(Array.from(event.target.files || []));
              }}
            />
            <FormText>
              Up to 10 files, max 20 MB each. Images, PDF, Office docs, TXT/CSV, ZIP/RAR/7Z are supported.
            </FormText>

            {messageFiles.length > 0 && (
              <div className="list-group mt-3">
                {messageFiles.map((file, index) => (
                  <div key={`${file.name}-${file.size}-${index}`} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div className="d-flex align-items-start gap-2">
                        <i className="bx bx-paperclip font-size-18 text-primary mt-1" />
                        <div>
                          <div className="fw-medium">{file.name}</div>
                          <small className="text-muted">{formatFileSize(file.size)}</small>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        onClick={() => onRemoveMessageFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Col>

          <Col lg={4} className="d-flex flex-column justify-content-between">
            {canViewInternalNotes && (
              <div className="form-check form-switch mb-3">
                <Input
                  type="switch"
                  id="internalNote"
                  checked={isInternalNote}
                  onChange={onToggleInternalNote}
                />
                <Label htmlFor="internalNote" className="form-check-label">
                  Internal note
                </Label>
                <div>
                  <small className="text-muted">
                    Visible only to staff and hidden from the ticket creator.
                  </small>
                </div>
              </div>
            )}

            <div className="d-flex flex-column align-items-lg-end gap-2">
              <small className="text-muted">Keep replies short, specific, and action-oriented.</small>
              <Button
                type="submit"
                color={isInternalNote ? "warning" : "primary"}
                disabled={messageComposerDisabled}
                className="chat-send"
              >
                <i className="bx bx-send me-1" />
                {sendingMessage ? "Sending..." : isInternalNote ? "Add internal note" : "Send message"}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </CardBody>
  </Card>
);

type ActivityFeedProps = {
  activitiesLoading: boolean;
  activities: ActivityItem[];
};

const ActivityFeed = ({
  activitiesLoading,
  activities,
}: ActivityFeedProps) => {
  if (activitiesLoading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" /> Loading activity...
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <Alert color="light" className="mb-0">
        No activity recorded yet.
      </Alert>
    );
  }

  return (
    <>
      {activities.map((activity, index) => (
        <div key={activity.id} className={`list-group-item px-0 ${index === 0 ? "pt-0" : ""}`}>
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div className="d-flex gap-3">
              <div className="avatar-xs">
                <span className="avatar-title rounded-circle bg-light text-primary">
                  {getUserInitials(activity.user?.name || "System")}
                </span>
              </div>
              <div>
                <div className="fw-semibold">{activity.user?.name || "System"}</div>
                <div className="text-muted">{activity.description || activity.action}</div>
              </div>
            </div>
            <small className="text-muted">{formatDateTime(activity.created_at)}</small>
          </div>
        </div>
      ))}
    </>
  );
};

type CannedResponsesPanelProps = {
  showCannedResponses: boolean;
  canUseCannedResponses: boolean;
  canCreateCannedResponses: boolean;
  canSendMessage: boolean;
  currentCategoryLabel: string;
  cannedResponses: CannedResponse[];
  canEditCannedResponse: (template: CannedResponse) => boolean;
  canDeleteCannedResponse: (template: CannedResponse) => boolean;
  onToggleCannedResponses: () => void;
  onSelectCannedResponse: (template: CannedResponse) => void;
  onOpenCreateCannedResponse: () => void;
  onOpenEditCannedResponse: (template: CannedResponse) => void;
  onDeleteCannedResponse: (template: CannedResponse) => void;
};

const CannedResponsesPanel = ({
  showCannedResponses,
  canUseCannedResponses,
  canCreateCannedResponses,
  canSendMessage,
  currentCategoryLabel,
  cannedResponses,
  canEditCannedResponse,
  canDeleteCannedResponse,
  onToggleCannedResponses,
  onSelectCannedResponse,
  onOpenCreateCannedResponse,
  onOpenEditCannedResponse,
  onDeleteCannedResponse,
}: CannedResponsesPanelProps) => {
  if (!canUseCannedResponses || !showCannedResponses) {
    return null;
  }

  return (
    <div className="border rounded p-3 mt-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h5 className="mb-1">Templates</h5>
          <small className="text-muted">
            Shared templates and your personal templates for {currentCategoryLabel.toLowerCase()} are shown here.
          </small>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {canCreateCannedResponses && (
            <Button color="primary" size="sm" onClick={onOpenCreateCannedResponse}>
              <i className="bx bx-plus me-1" />
              New template
            </Button>
          )}
          <Button color="light" size="sm" onClick={onToggleCannedResponses}>
            <i className="bx bx-copy-alt me-1" />
            Hide templates
          </Button>
        </div>
      </div>

      <div className="list-group">
        {cannedResponses.length === 0 ? (
          <div className="list-group-item text-muted">No templates available for this category.</div>
        ) : (
          cannedResponses.map((template) => (
            <div key={template.id} className="list-group-item">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div className="flex-grow-1">
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                    <div className="fw-semibold">{template.title}</div>
                    {template.is_shared && (
                      <Badge color="primary">Shared</Badge>
                    )}
                    <Badge color="light" className="text-dark border">
                      {template.category_id ? currentCategoryLabel : "All categories"}
                    </Badge>
                  </div>
                  <small className="text-muted d-block">
                    {template.body.length > 160 ? `${template.body.substring(0, 160)}...` : template.body}
                  </small>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {canSendMessage && (
                    <Button color="light" size="sm" onClick={() => onSelectCannedResponse(template)}>
                      Use
                    </Button>
                  )}
                  {canEditCannedResponse(template) && (
                    <Button color="light" size="sm" onClick={() => onOpenEditCannedResponse(template)}>
                      Edit
                    </Button>
                  )}
                  {canDeleteCannedResponse(template) && (
                    <Button color="danger" outline size="sm" onClick={() => onDeleteCannedResponse(template)}>
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

type TicketConversationPanelProps = {
  activeTab: TicketTab;
  groupedMessages: MessageGroup[];
  messages: TicketMessage[];
  messagesLoading: boolean;
  messagesEndRef: RefObject<HTMLDivElement>;
  conversationWrapRef: RefObject<HTMLDivElement>;
  attachmentPreviewUrls: Record<number, string>;
  previewFailedIds: Record<number, boolean>;
  currentUserId: number;
  typingUser: string | null;
  onlineUsersCount: number;
  loadOlderAvailable: boolean;
  canSendMessage: boolean;
  isConversationLocked: boolean;
  canViewInternalNotes: boolean;
  canUseCannedResponses: boolean;
  canCreateCannedResponses: boolean;
  showCannedResponses: boolean;
  cannedResponses: CannedResponse[];
  currentCannedResponseCategoryLabel: string;
  messageBody: string;
  messageFileInputKey: number;
  messageFiles: File[];
  isInternalNote: boolean;
  messageComposerDisabled: boolean;
  sendingMessage: boolean;
  sendMessageShortcutLabel: string;
  activitiesLoading: boolean;
  activities: ActivityItem[];
  onShowConversation: () => void;
  onShowActivity: () => void;
  onLoadOlder: () => void;
  onDownloadAttachment: (attachment: TicketAttachment) => void;
  onOpenImagePreview: (attachment: TicketAttachment) => void;
  onEnsurePreview: (attachment: TicketAttachment) => void;
  onToggleCannedResponses: () => void;
  onSelectCannedResponse: (template: CannedResponse) => void;
  onOpenCreateCannedResponse: () => void;
  onOpenEditCannedResponse: (template: CannedResponse) => void;
  onDeleteCannedResponse: (template: CannedResponse) => void;
  canEditCannedResponse: (template: CannedResponse) => boolean;
  canDeleteCannedResponse: (template: CannedResponse) => boolean;
  onMessageBodyChange: (value: string) => void;
  onMessageInputKeyDown: KeyboardEventHandler<HTMLInputElement>;
  onMessageFilesChange: (files: File[]) => void;
  onRemoveMessageFile: (fileIndex: number) => void;
  onToggleInternalNote: () => void;
  onSubmitMessage: FormEventHandler<HTMLFormElement>;
};

const TicketConversationPanel = ({
  activeTab,
  groupedMessages,
  messages,
  messagesLoading,
  messagesEndRef,
  conversationWrapRef,
  attachmentPreviewUrls,
  previewFailedIds,
  currentUserId,
  typingUser,
  onlineUsersCount,
  loadOlderAvailable,
  canSendMessage,
  isConversationLocked,
  canViewInternalNotes,
  canUseCannedResponses,
  canCreateCannedResponses,
  showCannedResponses,
  cannedResponses,
  currentCannedResponseCategoryLabel,
  messageBody,
  messageFileInputKey,
  messageFiles,
  isInternalNote,
  messageComposerDisabled,
  sendingMessage,
  sendMessageShortcutLabel,
  activitiesLoading,
  activities,
  onShowConversation,
  onShowActivity,
  onLoadOlder,
  onDownloadAttachment,
  onOpenImagePreview,
  onEnsurePreview,
  onToggleCannedResponses,
  onSelectCannedResponse,
  onOpenCreateCannedResponse,
  onOpenEditCannedResponse,
  onDeleteCannedResponse,
  canEditCannedResponse,
  canDeleteCannedResponse,
  onMessageBodyChange,
  onMessageInputKeyDown,
  onMessageFilesChange,
  onRemoveMessageFile,
  onToggleInternalNote,
  onSubmitMessage,
}: TicketConversationPanelProps) => (
  <Card className="h-100">
    <CardBody>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <ul className="nav nav-pills gap-2">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "conversation" ? "active" : ""}`}
              onClick={onShowConversation}
            >
              Conversation <Badge color={activeTab === "conversation" ? "light" : "primary"} className="ms-1">{messages.length}</Badge>
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "activity" ? "active" : ""}`}
              onClick={onShowActivity}
            >
              Activity <Badge color={activeTab === "activity" ? "light" : "primary"} className="ms-1">{activities.length}</Badge>
            </button>
          </li>
        </ul>

        {activeTab === "conversation" && (
          <div className="d-flex flex-wrap align-items-center gap-2">
            {typingUser && <small className="text-muted">{typingUser} is typing...</small>}
            <Badge color="light" className="text-dark border">
              Online: {onlineUsersCount}
            </Badge>
            {canUseCannedResponses && (
              <Button color="light" size="sm" onClick={onToggleCannedResponses}>
                <i className="bx bx-copy-alt me-1" />
                {showCannedResponses ? "Hide templates" : "Templates"}
              </Button>
            )}
            {loadOlderAvailable && (
              <Button color="light" size="sm" onClick={onLoadOlder} disabled={messagesLoading}>
                {messagesLoading ? "Loading..." : "Load older"}
              </Button>
            )}
          </div>
        )}
      </div>

      {activeTab === "conversation" ? (
        <>
          <div
            ref={conversationWrapRef}
            className="chat-conversation overflow-auto border rounded p-3"
          >
            {messagesLoading && messages.length === 0 ? (
              <div className="text-center py-4">
                <Spinner size="sm" /> Loading conversation...
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bx bx-message-square-dots d-block font-size-24 mb-2" />
                <div className="fw-semibold text-dark mb-1">No conversation yet</div>
                <div>Start the thread with a clear update or a focused question.</div>
              </div>
            ) : (
              <ul className="list-unstyled mb-0">
                {groupedMessages.map((group) => (
                  <React.Fragment key={group.key}>
                    <li className="chat-day-title">
                      <span className="title badge rounded-pill bg-white text-muted border">
                        {group.label}
                      </span>
                    </li>
                    {group.messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        previewUrls={attachmentPreviewUrls}
                        previewFailed={previewFailedIds}
                        onDownloadAttachment={onDownloadAttachment}
                        onOpenImagePreview={onOpenImagePreview}
                        onEnsurePreview={onEnsurePreview}
                        currentUserId={currentUserId}
                      />
                    ))}
                  </React.Fragment>
                ))}
                <li>
                  <div ref={messagesEndRef} />
                </li>
              </ul>
            )}
          </div>

          <CannedResponsesPanel
            showCannedResponses={showCannedResponses}
            canUseCannedResponses={canUseCannedResponses}
            canCreateCannedResponses={canCreateCannedResponses}
            canSendMessage={canSendMessage}
            currentCategoryLabel={currentCannedResponseCategoryLabel}
            cannedResponses={cannedResponses}
            canEditCannedResponse={canEditCannedResponse}
            canDeleteCannedResponse={canDeleteCannedResponse}
            onToggleCannedResponses={onToggleCannedResponses}
            onSelectCannedResponse={onSelectCannedResponse}
            onOpenCreateCannedResponse={onOpenCreateCannedResponse}
            onOpenEditCannedResponse={onOpenEditCannedResponse}
            onDeleteCannedResponse={onDeleteCannedResponse}
          />

          {canSendMessage ? (
            <MessageComposer
              canViewInternalNotes={canViewInternalNotes}
              isInternalNote={isInternalNote}
              messageBody={messageBody}
              messageFileInputKey={messageFileInputKey}
              messageFiles={messageFiles}
              sendingMessage={sendingMessage}
              messageComposerDisabled={messageComposerDisabled}
              sendMessageShortcutLabel={sendMessageShortcutLabel}
              onMessageBodyChange={onMessageBodyChange}
              onMessageInputKeyDown={onMessageInputKeyDown}
              onMessageFilesChange={onMessageFilesChange}
              onRemoveMessageFile={onRemoveMessageFile}
              onToggleInternalNote={onToggleInternalNote}
              onSubmit={onSubmitMessage}
            />
          ) : isConversationLocked ? (
            <Alert color="warning" className="mt-3 mb-0">
              Conversation is locked because the ticket is resolved or closed. Reopen the ticket to continue the thread.
            </Alert>
          ) : (
            <Alert color="warning" className="mt-3 mb-0">
              Reply is available only for the ticket creator and the current assignee.
            </Alert>
          )}
        </>
      ) : (
        <div className="list-group list-group-flush">
          <ActivityFeed activitiesLoading={activitiesLoading} activities={activities} />
        </div>
      )}
    </CardBody>
  </Card>
);

export default TicketConversationPanel;
